/**
 * Export utilities for CSV, Excel, and PDF
 */
import { getBackendOrigin, resolveBackendUrl } from "../helpers/serverUrl";

/**
 * Export data to CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects { key: 'field', label: 'Display Name' }
 * @param {String} filename - Name of the file to download
 */
export const exportToCSV = (data, headers, filename = "export.csv") => {
    if (!data || data.length === 0) {
        alert("No data to export");
        return;
    }

    // Create CSV header row
    const headerRow = headers.map((h) => `"${h.label}"`).join(",");

    // Create CSV data rows
    const dataRows = data.map((row) => {
        return headers
            .map((h) => {
                let value = row[h.key];
                // Handle nested objects
                if (value && typeof value === "object") {
                    value = value.name || value.code || JSON.stringify(value);
                }
                // Handle null/undefined
                if (value === null || value === undefined) {
                    value = "";
                }
                // Escape quotes and wrap in quotes
                value = String(value).replace(/"/g, '""');
                return `"${value}"`;
            })
            .join(",");
    });

    // Combine header and data
    const csvContent = [headerRow, ...dataRows].join("\n");

    // Add BOM for UTF-8 to ensure Excel opens it correctly
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });

    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Download image from URL and convert to buffer (for browser)
 * Includes authentication headers if URL is from the API server
 */
const downloadImage = async (url) => {
    try {
        // Get auth token if available
        const authUser = localStorage.getItem('authUser');
        let token = null;
        if (authUser) {
            try {
                const parsed = JSON.parse(authUser);
                token = parsed.token || parsed.accessToken;
            } catch (e) {
                // Ignore parse errors
            }
        }

        // Check if URL points to the same backend origin used by API requests.
        const backendOrigin = getBackendOrigin();
        const resolvedImageUrl = resolveBackendUrl(url);
        let isApiUrl = false;
        try {
            isApiUrl = new URL(resolvedImageUrl).origin === new URL(backendOrigin).origin;
        } catch (e) {
            isApiUrl = false;
        }

        // Prepare headers
        const headers = {};
        if (token && isApiUrl) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(resolvedImageUrl, { 
            mode: 'cors',
            headers: headers,
            credentials: 'include' // Include cookies if needed
        });
        
        if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        return arrayBuffer;
    } catch (error) {
        console.warn(`Failed to download image from ${url}:`, error);
        return null;
    }
};

/**
 * Export data to Excel (XLSX) file with image support
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects { key: 'field', label: 'Display Name' }
 * @param {String} filename - Name of the file to download
 */
export const exportToExcel = async (data, headers, filename = "export.xlsx") => {
    if (!data || data.length === 0) {
        alert("No data to export");
        return;
    }

    // Try to use exceljs for image support, fallback to xlsx
    try {
        const ExcelJS = await import("exceljs");
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Sheet1");

        // Find the index of first_photo_url header if it exists
        const photoUrlHeaderIndex = headers.findIndex(h => h.key === 'first_photo_url');
        const hasPhotoColumn = photoUrlHeaderIndex >= 0;

        // Prepare header row - insert "Image" column after photo URL if exists
        const headerLabels = headers.map((h) => h.label);
        if (hasPhotoColumn) {
            headerLabels.splice(photoUrlHeaderIndex + 1, 0, "Image");
        }

        // Add headers
        const headerRow = worksheet.addRow(headerLabels);
        headerRow.font = { bold: true };
        headerRow.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF0147C8' }
        };
        headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };

        // Add data rows
        const imagePromises = [];
        data.forEach((row, rowIndex) => {
            const rowData = headers.map((h) => {
                let value = row[h.key];
                // Handle nested objects
                if (value && typeof value === "object") {
                    value = value.name || value.code || JSON.stringify(value);
                }
                return value || "";
            });
            
            // Insert empty cell for image column if photo column exists
            if (hasPhotoColumn) {
                rowData.splice(photoUrlHeaderIndex + 1, 0, ""); // Empty cell for image
            }
            
            const excelRow = worksheet.addRow(rowData);
            
            // If photo URL exists, download and add image
            if (hasPhotoColumn) {
                const photoUrl = row.first_photo_url;
                if (photoUrl && photoUrl !== '-' && photoUrl.startsWith('http')) {
                    // Add hyperlink to URL cell
                    excelRow.getCell(photoUrlHeaderIndex + 1).value = {
                        text: photoUrl,
                        hyperlink: photoUrl
                    };
                    excelRow.getCell(photoUrlHeaderIndex + 1).font = {
                        color: { argb: 'FF0563C1' },
                        underline: true
                    };
                    
                    // Download image and add to next column
                    const imagePromise = downloadImage(photoUrl).then(imageBuffer => {
                        if (imageBuffer) {
                            try {
                                const imageColumnIndex = photoUrlHeaderIndex + 2; // Next column after URL
                                // Get file extension from URL
                                const extension = photoUrl.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)?.[1]?.toLowerCase() || 'png';
                                
                                const imageId = workbook.addImage({
                                    buffer: imageBuffer,
                                    extension: extension,
                                });
                                
                                // Insert image in the cell next to URL
                                // Note: row is 0-indexed in exceljs, but we need to account for header row
                                worksheet.addImage(imageId, {
                                    tl: { col: imageColumnIndex - 1, row: rowIndex + 1 },
                                    ext: { width: 100, height: 100 }
                                });
                                
                                // Set row height to accommodate image
                                excelRow.height = 100;
                                
                                console.log(`✅ Added image for row ${rowIndex + 1} from ${photoUrl}`);
                            } catch (imgError) {
                                console.error(`Error adding image to Excel for row ${rowIndex + 1}:`, imgError);
                            }
                        } else {
                            console.warn(`⚠️ No image buffer for row ${rowIndex + 1} from ${photoUrl}`);
                        }
                    }).catch(err => {
                        console.warn(`Failed to add image for row ${rowIndex + 1}:`, err);
                    });
                    
                    imagePromises.push(imagePromise);
                }
            }
        });

        // Wait for all images to be downloaded and added
        await Promise.all(imagePromises);

        // Set column widths
        headers.forEach((h, idx) => {
            // Adjust index if image column was inserted
            const actualIdx = hasPhotoColumn && idx > photoUrlHeaderIndex ? idx + 1 : idx;
            const column = worksheet.getColumn(actualIdx + 1);
            
            if (h.key === 'first_photo_url') {
                column.width = 50;
            } else {
                const maxLength = Math.max(
                    h.label.length,
                    ...data.map((row) => {
                        let value = row[h.key];
                        if (value && typeof value === "object") {
                            value = value.name || value.code || "";
                        }
                        return String(value || "").length;
                    })
                );
                column.width = Math.min(Math.max(maxLength + 2, 10), 50);
            }
        });

        // Set image column width if photo column exists
        if (hasPhotoColumn) {
            const imageColumnIndex = photoUrlHeaderIndex + 2;
            worksheet.getColumn(imageColumnIndex).width = 15; // Width for image column
        }

        // Write file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        // Fallback to xlsx if exceljs is not available
        console.warn("exceljs not available, falling back to xlsx:", error);
        const XLSX = await import("xlsx");
        
        // Find the index of first_photo_url header if it exists
        const photoUrlHeaderIndex = headers.findIndex(h => h.key === 'first_photo_url');
        
        // Create worksheet data
        const worksheetData = [
            headers.map((h) => h.label), // Header row
            ...data.map((row) =>
                headers.map((h) => {
                    let value = row[h.key];
                    // Handle nested objects
                    if (value && typeof value === "object") {
                        value = value.name || value.code || JSON.stringify(value);
                    }
                    return value || "";
                })
            ),
        ];

        // Create workbook and worksheet
        const ws = XLSX.utils.aoa_to_sheet(worksheetData);

        // Add hyperlinks for photo URLs if first_photo_url column exists
        if (photoUrlHeaderIndex >= 0) {
            data.forEach((row, rowIndex) => {
                const photoUrl = row.first_photo_url;
                if (photoUrl && photoUrl !== '-' && photoUrl.startsWith('http')) {
                    // Excel cell reference (1-based, +1 for header row)
                    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: photoUrlHeaderIndex });
                    if (!ws[cellAddress]) return;
                    
                    // Add hyperlink
                    ws[cellAddress].l = {
                        Target: photoUrl,
                        Tooltip: `View image: ${photoUrl}`
                    };
                    ws[cellAddress].s = {
                        font: { color: { rgb: "0563C1" }, underline: true }
                    };
                }
            });
        }

        // Auto-size columns
        const colWidths = headers.map((h, idx) => {
            const maxLength = Math.max(
                h.label.length,
                ...data.map((row) => {
                    let value = row[h.key];
                    if (value && typeof value === "object") {
                        value = value.name || value.code || "";
                    }
                    return String(value || "").length;
                })
            );
            // Make photo URL column wider
            if (h.key === 'first_photo_url') {
                return { wch: 50 };
            }
            return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
        });
        ws["!cols"] = colWidths;

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

        // Write file
        XLSX.writeFile(wb, filename);
    }
};

export const exportToBrandedExcel = async (
    data,
    headers,
    filename = "export.xlsx",
    options = {}
) => {
    if (!data || data.length === 0) {
        alert("No data to export");
        return;
    }

    const {
        sheetName = "Sheet1",
        title = "TD Property",
        subtitle = "",
        logoPath = null,
        numericColumns = [],
        hyperlinkColumns = [],
    } = options;

    try {
        const ExcelJS = await import("exceljs");
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(sheetName);

        const totalColumns = Math.max(headers.length, 1);
        const lastColumnLetter = worksheet.getColumn(totalColumns).letter;
        const headerRowNumber = 3;
        const dataStartRowNumber = headerRowNumber + 1;

        worksheet.mergeCells(`B1:${lastColumnLetter}1`);
        const titleCell = worksheet.getCell("B1");
        titleCell.value = title;
        titleCell.font = { bold: true, size: 18, color: { argb: "FF0147C8" } };
        titleCell.alignment = { horizontal: "center", vertical: "middle" };

        worksheet.mergeCells(`B2:${lastColumnLetter}2`);
        const subtitleCell = worksheet.getCell("B2");
        subtitleCell.value = subtitle;
        subtitleCell.font = { bold: true, size: 12, color: { argb: "FF444444" } };
        subtitleCell.alignment = { horizontal: "center", vertical: "middle" };

        if (logoPath) {
            const logoBuffer = await downloadImage(logoPath);
            if (logoBuffer) {
                const logoExtension = logoPath.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)?.[1]?.toLowerCase() || "png";
                const logoId = workbook.addImage({
                    buffer: logoBuffer,
                    extension: logoExtension === "jpg" ? "jpeg" : logoExtension,
                });
                worksheet.addImage(logoId, {
                    tl: { col: 0, row: 0 },
                    ext: { width: 72, height: 72 },
                });
            }
        }

        worksheet.getRow(1).height = 30;
        worksheet.getRow(2).height = 22;
        worksheet.getRow(headerRowNumber).height = 22;

        const headerLabels = headers.map((h) => h.label);
        const headerRow = worksheet.getRow(headerRowNumber);
        headerRow.values = headerLabels;
        headerLabels.forEach((_, idx) => {
            const cell = worksheet.getCell(headerRowNumber, idx + 1);
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FF0147C8" }
            };
            cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        data.forEach((row, index) => {
            const rowData = headers.map((h) => {
                let value = row[h.key];
                if (value && typeof value === "object") {
                    value = value.name || value.code || JSON.stringify(value);
                }
                return value ?? "";
            });
            const excelRow = worksheet.getRow(dataStartRowNumber + index);
            excelRow.values = rowData;

            headers.forEach((header, headerIndex) => {
                const cell = worksheet.getCell(dataStartRowNumber + index, headerIndex + 1);
                if (numericColumns.includes(header.key)) {
                    const numericValue = Number(row?.[header.key] ?? 0);
                    if (Number.isFinite(numericValue)) {
                        cell.value = numericValue;
                        cell.numFmt = "#,##0";
                    }
                }

                if (hyperlinkColumns.includes(header.key)) {
                    const rawValue = row?.[header.key];
                    if (typeof rawValue === "string" && /^https?:\/\//i.test(rawValue.trim())) {
                        cell.value = {
                            text: rawValue,
                            hyperlink: rawValue,
                        };
                        cell.font = {
                            color: { argb: "FF0563C1" },
                            underline: true,
                        };
                    }
                }
            });
        });

        headers.forEach((header, idx) => {
            const column = worksheet.getColumn(idx + 1);
            const maxLength = Math.max(
                header.label.length,
                ...data.map((row) => String(row?.[header.key] ?? "").length)
            );
            column.width = Math.min(Math.max(maxLength + 4, 14), 40);
        });

        worksheet.views = [{ state: "frozen", ySplit: headerRowNumber }];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.warn("Branded excel export failed, falling back to default excel export:", error);
        await exportToExcel(data, headers, filename);
    }
};

/**
 * Export data to PDF file with logo and metadata
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects { key: 'field', label: 'Display Name' }
 * @param {String} filename - Name of the file to download
 * @param {String} title - Title of the PDF document
 * @param {Object} metadata - Additional metadata { clientName, projectName, exportedBy, logoPath }
 */
export const exportToPDF = async (
    data, 
    headers, 
    filename = "export.pdf", 
    title = "Asset Report",
    metadata = {}
) => {
    if (!data || data.length === 0) {
        alert("No data to export");
        return;
    }

    const { clientName, projectName, exportedBy, logoPath } = metadata;

    try {
        // Dynamic import of jsPDF and autoTable
        const jsPDFModule = await import("jspdf");
        // jsPDF v2.x uses default export
        const jsPDF = jsPDFModule.default;
        
        // Import autoTable plugin - it extends jsPDF automatically
        await import("jspdf-autotable");
            // Create PDF document - use landscape for better visibility of many columns
            const doc = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4"
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            let currentY = 15;

            // Add logo if provided
            if (logoPath) {
                try {
                    // Logo on the left - original height (15mm) but reduced width (25mm instead of 30mm)
                    doc.addImage(logoPath, "PNG", 14, 10, 25, 15);
                    currentY = 30;
                } catch (error) {
                    console.warn("Could not add logo:", error);
                    currentY = 15;
                }
            }

            // Add company name at top-right
            doc.setFontSize(18);
            doc.setFont(undefined, "bold");
            doc.setTextColor(1, 71, 200); // BRAND_BLUE
            doc.text("TD PROPERTY DEVELOPERS", pageWidth - 14, 15, { align: "right" });
            
            // Add title
            doc.setFontSize(14);
            doc.setTextColor(0, 0, 0);
            doc.text(title, pageWidth - 14, 22, { align: "right" });

            currentY = Math.max(currentY, 30);

            // Add Client and Project names prominently (like company name)
            if (clientName || projectName) {
                doc.setFontSize(11);
                doc.setFont(undefined, "bold");
                doc.setTextColor(0, 0, 0);
                
                if (clientName) {
                    doc.text(`Client: ${clientName}`, 14, currentY);
                    currentY += 6;
                }
                
                if (projectName) {
                    doc.text(`Project: ${projectName}`, 14, currentY);
                    currentY += 6;
                }
                
                currentY += 2; // Extra spacing
            }

            // Add export date and time
            doc.setFontSize(9);
            doc.setFont(undefined, "normal");
            doc.setTextColor(80, 80, 80);

            // Export date and time
            const exportDateTime = new Date().toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            doc.setFont(undefined, "bold");
            doc.text("Exported:", 14, currentY);
            doc.setFont(undefined, "normal");
            doc.text(exportDateTime, 35, currentY);
            currentY += 8;

            // Add separator line
            doc.setDrawColor(200, 200, 200);
            doc.line(14, currentY, pageWidth - 14, currentY);
            currentY += 5;

            // Prepare table data
            const tableHeaders = headers.map(h => h.label);
            const photoUrlHeaderIndex = headers.findIndex(h => h.key === 'first_photo_url');
            const hasPhotoColumn = photoUrlHeaderIndex >= 0;
            
            // Download all images first
            const imageDataMap = new Map(); // Map rowIndex -> { base64, extension }
            
            if (hasPhotoColumn) {
                const imagePromises = data.map(async (row, rowIndex) => {
                    const photoUrl = row.first_photo_url;
                    if (photoUrl && photoUrl !== '-' && photoUrl.startsWith('http')) {
                        try {
                            const imageBuffer = await downloadImage(photoUrl);
                            if (imageBuffer) {
                                // Convert ArrayBuffer to base64
                                const uint8Array = new Uint8Array(imageBuffer);
                                const binaryString = Array.from(uint8Array, byte => String.fromCharCode(byte)).join('');
                                const base64 = btoa(binaryString);
                                
                                // Get file extension
                                const extension = photoUrl.match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i)?.[1]?.toLowerCase() || 'png';
                                imageDataMap.set(rowIndex, { base64, extension, imageData: `data:image/${extension};base64,${base64}` });
                                console.log(`✅ Downloaded image for row ${rowIndex + 1}`);
                            }
                        } catch (err) {
                            console.warn(`Failed to download image for row ${rowIndex + 1}:`, err);
                        }
                    }
                });
                
                await Promise.all(imagePromises);
            }
            
            // Prepare table data
            const tableData = data.map((row, rowIndex) => {
                const rowData = headers.map(h => {
                    let value = row[h.key];
                    // Handle nested objects
                    if (value && typeof value === "object") {
                        value = value.name || value.code || "";
                    }
                    // Handle boolean values
                    if (typeof value === "boolean") {
                        value = value ? "Yes" : "No";
                    }
                    // Handle null/undefined
                    if (value === null || value === undefined) {
                        value = "-";
                    }
                    return String(value);
                });
                
                // Insert image placeholder if photo exists
                if (hasPhotoColumn) {
                    if (imageDataMap.has(rowIndex)) {
                        rowData.splice(photoUrlHeaderIndex + 1, 0, "[Image]");
                    } else {
                        rowData.splice(photoUrlHeaderIndex + 1, 0, "-");
                    }
                }
                
                return rowData;
            });
            
            // Add "Image" header if photo column exists
            if (hasPhotoColumn) {
                tableHeaders.splice(photoUrlHeaderIndex + 1, 0, "Image");
            }

            // Add table - optimized for landscape
            doc.autoTable({
                head: [tableHeaders],
                body: tableData,
                startY: currentY,
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    overflow: "linebreak",
                    cellWidth: "wrap",
                    valign: "middle"
                },
                headStyles: {
                    fillColor: [1, 71, 200], // BRAND_BLUE
                    textColor: 255,
                    fontStyle: "bold",
                    halign: "center",
                    fontSize: 9
                },
                alternateRowStyles: {
                    fillColor: [245, 245, 245]
                },
                margin: { top: currentY, left: 10, right: 10, bottom: 20 },
                didDrawCell: (data) => {
                    // Add images in the Image column
                    if (hasPhotoColumn && data.column.index === photoUrlHeaderIndex + 1 && data.row.index >= 0) {
                        const rowIndex = data.row.index;
                        if (imageDataMap.has(rowIndex)) {
                            const { imageData, extension } = imageDataMap.get(rowIndex);
                            try {
                                const cellX = data.cell.x;
                                const cellY = data.cell.y;
                                const cellWidth = data.cell.width;
                                const cellHeight = data.cell.height;
                                
                                // Add image in the cell (centered)
                                const imageSize = Math.min(cellWidth - 2, cellHeight - 2, 20); // Max 20mm
                                const imageX = cellX + (cellWidth - imageSize) / 2;
                                const imageY = cellY + (cellHeight - imageSize) / 2;
                                
                                doc.addImage(imageData, extension.toUpperCase(), imageX, imageY, imageSize, imageSize);
                            } catch (imgError) {
                                console.error(`Error adding image to PDF cell for row ${rowIndex}:`, imgError);
                            }
                        }
                    }
                },
                didDrawPage: (data) => {
                    // Footer
                    const footerY = pageHeight - 12;
                    
                    // Draw footer line
                    doc.setDrawColor(200, 200, 200);
                    doc.line(14, footerY - 3, pageWidth - 14, footerY - 3);
                    
                    doc.setFontSize(8);
                    doc.setTextColor(100, 100, 100);
                    doc.setFont(undefined, "normal");
                    
                    // Exported by (left)
                    if (exportedBy) {
                        doc.text(`Exported by: ${exportedBy}`, 14, footerY);
                    }
                    
                    // Page number (center)
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.text(
                        `Page ${data.pageNumber} of ${pageCount}`,
                        pageWidth / 2,
                        footerY,
                        { align: "center" }
                    );
                    
                    // TD Property Developers (right)
                    doc.text("TD Property Developers", pageWidth - 14, footerY, { align: "right" });
                }
            });

            // Save the PDF
            doc.save(filename);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to generate PDF. Please make sure jsPDF library is installed.");
        }
};
